// src/components/layout/ExportExcelDialog.jsx
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState, useEffect } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { toast } from 'react-hot-toast';

// State FIPS to abbreviation mapping for export
const FIPS_TO_ABBR = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO', '09': 'CT',
  '10': 'DE', '11': 'DC', '12': 'FL', '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL',
  '18': 'IN', '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME', '24': 'MD',
  '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS', '29': 'MO', '30': 'MT', '31': 'NE',
  '32': 'NV', '33': 'NH', '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC', '46': 'SD',
  '47': 'TN', '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV',
  '55': 'WI', '56': 'WY'
};

// Map internal ma_type to display type for Excel
const MA_TYPE_TO_DISPLAY = {
  'zip': 'ZIP',
  'place': 'Place',
  'county': 'County',
  'tract': 'Tract',
  'blockgroup': 'Block Group',
  'block': 'Block',
  'cbsa': 'CBSA',
  'state': 'State',
  'radius': 'Radius',
  'drivetime': 'Drive Time',
  'custom': 'Custom',
  'site_location': 'Site Location',
  'usa': 'USA'
};

// Extract state from locations or market area data
const extractState = (marketArea) => {
  // First check if there's a state field directly on the market area
  if (marketArea.state) {
    return marketArea.state;
  }

  // For tract, block, blockgroup - extract state from FIPS code
  if (['tract', 'blockgroup', 'block'].includes(marketArea.ma_type)) {
    const locations = marketArea.locations || [];
    if (locations.length > 0) {
      const firstLoc = locations[0];
      const locId = firstLoc.id || firstLoc.name || '';
      // First 2 digits of FIPS code are state code
      if (locId.length >= 2) {
        const stateFips = locId.substring(0, 2);
        return FIPS_TO_ABBR[stateFips] || stateFips;
      }
    }
  }

  // Check if any location has state info
  const locations = marketArea.locations || [];
  for (const loc of locations) {
    if (loc.state) {
      return loc.state;
    }
    // Some locations store state in attributes
    if (loc.attributes?.STATE) {
      const stateFips = loc.attributes.STATE.toString().padStart(2, '0');
      return FIPS_TO_ABBR[stateFips] || loc.attributes.STATE;
    }
  }

  // Default to empty if we can't determine
  return '';
};

// Helper to convert hex color to ExcelJS ARGB format
const hexToARGB = (hex) => {
  if (!hex) return null;
  // Remove # if present
  const cleanHex = hex.replace('#', '').toUpperCase();
  // Ensure 6 characters
  if (cleanHex.length !== 6) return null;
  // Add FF for full opacity
  return 'FF' + cleanHex;
};

export default function ExportExcelDialog({ isOpen, onClose, marketAreas }) {
  const [fileName, setFileName] = useState('market_areas_export');
  const [selectedMAIds, setSelectedMAIds] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  // Filter to only show exportable types (location-based types)
  const exportableTypes = ['zip', 'place', 'county', 'tract', 'blockgroup', 'block', 'cbsa', 'state'];
  const exportableMarketAreas = marketAreas.filter(ma => exportableTypes.includes(ma.ma_type));

  useEffect(() => {
    // Initially select all exportable market areas
    setSelectedMAIds(exportableMarketAreas.map(ma => ma.id));
  }, [marketAreas]);

  const handleToggleMA = (maId) => {
    setSelectedMAIds((prev) =>
      prev.includes(maId) ? prev.filter((id) => id !== maId) : [...prev, maId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedMAIds(exportableMarketAreas.map(ma => ma.id));
    } else {
      setSelectedMAIds([]);
    }
  };

  const handleExport = async () => {
    if (selectedMAIds.length === 0) {
      toast.error('Please select at least one market area to export');
      return;
    }

    setIsExporting(true);
    const loadingToast = toast.loading('Generating Excel file...');

    try {
      // Get selected market areas
      const selectedMAs = marketAreas.filter(ma => selectedMAIds.includes(ma.id));

      // Template format row indices (1-based for Excel)
      const MARKET_NAME_ROW = 5;
      const SHORT_NAME_ROW = 7;
      const COLOR_ROW = 9;
      const MA_TYPE_ROW = 11;
      const STATE_ROW = 13;
      const COUNTY_ROW = 15;
      const NOTES_ROW = 17;
      const VINTAGE_ROW = 19;
      const DEFINITION_START_ROW = 21;

      // Market area columns: D, F, H, J, L, N, P, R, T (columns 4, 6, 8, 10, 12, 14, 16, 18, 20)
      const MA_COLUMNS = [4, 6, 8, 10, 12, 14, 16, 18, 20];

      // Create a new workbook from scratch
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Market Area Tool';
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet('Market Areas');

      // Set column widths
      worksheet.getColumn(1).width = 5;  // A - row numbers
      worksheet.getColumn(2).width = 25; // B - labels
      worksheet.getColumn(3).width = 3;  // C - spacer
      MA_COLUMNS.forEach(colNum => {
        worksheet.getColumn(colNum).width = 20;
      });

      // Add row labels in column B (Definition handled separately for multiple rows)
      const rowLabels = {
        [MARKET_NAME_ROW]: 'Full Market Area Name',
        [SHORT_NAME_ROW]: 'Optional Short Name (PMA, CMA)',
        [COLOR_ROW]: 'Market Area Fill/Text Color',
        [MA_TYPE_ROW]: 'Definition Type',
        [STATE_ROW]: '* State',
        [COUNTY_ROW]: '** County',
        [NOTES_ROW]: 'Notes / Radius Lat/Long',
        [VINTAGE_ROW]: '*** Definition Vintage (Tract Only)'
      };

      // Style for label cells
      const labelStyle = {
        font: { bold: true },
        alignment: { horizontal: 'right', vertical: 'middle' }
      };

      // Border style for all data cells
      const thinBorder = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };

      // Light border for definition rows (gray separator lines)
      const lightBorder = {
        top: { style: 'thin', color: { argb: 'FFC0C0C0' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FFC0C0C0' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };

      // Add labels (no borders on labels)
      Object.entries(rowLabels).forEach(([row, label]) => {
        const cell = worksheet.getCell(parseInt(row), 2);
        cell.value = label;
        cell.font = labelStyle.font;
        cell.alignment = labelStyle.alignment;
      });

      // Add title
      const titleCell = worksheet.getCell(1, 2);
      titleCell.value = 'Market Area Definitions Export';
      titleCell.font = { bold: true, size: 14 };

      // Populate market area data
      selectedMAs.forEach((ma, index) => {
        if (index >= MA_COLUMNS.length) {
          console.warn(`Too many market areas to export, skipping ${ma.name} (max ${MA_COLUMNS.length})`);
          return;
        }

        const colNum = MA_COLUMNS[index];
        const displayType = MA_TYPE_TO_DISPLAY[ma.ma_type] || ma.ma_type;
        const state = extractState(ma);

        // Get colors from style_settings, with defaults
        const styleSettings = ma.style_settings || {};
        const fillColor = styleSettings.fillColor || styleSettings.fill || '#0078D4';
        const textColor = styleSettings.textColor || '#FFFFFF';

        // Debug: Log the actual values being exported
        console.log(`Exporting ${ma.name}: fillColor=${fillColor}, textColor=${textColor}`, styleSettings);

        // Market Name (Row 5)
        const nameCell = worksheet.getCell(MARKET_NAME_ROW, colNum);
        nameCell.value = ma.name || '';
        nameCell.font = { bold: true };
        nameCell.border = thinBorder;

        // Short Name (Row 7)
        const shortNameCell = worksheet.getCell(SHORT_NAME_ROW, colNum);
        shortNameCell.value = ma.short_name || ma.name || '';
        shortNameCell.border = thinBorder;

        // Fill/Text Color combined (Row 9) - cell background is fill color, text "Text Color" in text color
        const colorCell = worksheet.getCell(COLOR_ROW, colNum);
        colorCell.value = 'Text Color';

        // Apply background fill color
        const fillARGB = hexToARGB(fillColor);
        if (fillARGB) {
          colorCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: fillARGB }
          };
        }

        // Apply font color
        const textARGB = hexToARGB(textColor);
        if (textARGB) {
          colorCell.font = {
            color: { argb: textARGB }
          };
        }
        colorCell.border = thinBorder;

        // MA Type (Row 11)
        const typeCell = worksheet.getCell(MA_TYPE_ROW, colNum);
        typeCell.value = displayType;
        typeCell.border = thinBorder;

        // State (Row 13)
        const stateCell = worksheet.getCell(STATE_ROW, colNum);
        stateCell.value = state;
        stateCell.border = thinBorder;

        // County (Row 15) - leave empty
        const countyCell = worksheet.getCell(COUNTY_ROW, colNum);
        countyCell.value = '';
        countyCell.border = thinBorder;

        // Notes (Row 17)
        const notesCell = worksheet.getCell(NOTES_ROW, colNum);
        notesCell.value = ma.description || '';
        notesCell.border = thinBorder;

        // Definition Vintage (Row 19) - leave empty
        const vintageCell = worksheet.getCell(VINTAGE_ROW, colNum);
        vintageCell.value = '';
        vintageCell.border = thinBorder;

        // Definition values (Row 21+) - separate row for each location with light gray separator lines
        const locations = ma.locations || [];
        locations.forEach((loc, locIndex) => {
          const rowNum = DEFINITION_START_ROW + locIndex;
          const locValue = loc.id || loc.name || '';
          const locCell = worksheet.getCell(rowNum, colNum);
          locCell.value = locValue.toString().trim();

          // First row: black top, gray bottom
          // Last row: gray top, black bottom
          // Middle rows: gray top and bottom
          const isFirst = locIndex === 0;
          const isLast = locIndex === locations.length - 1;
          locCell.border = {
            top: { style: 'thin', color: { argb: isFirst ? 'FF000000' : 'FFC0C0C0' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: isLast ? 'FF000000' : 'FFC0C0C0' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          };
        });
      });

      // Add Definition label (no border)
      const locationLabel = worksheet.getCell(DEFINITION_START_ROW, 2);
      locationLabel.value = '**** Definition';
      locationLabel.font = labelStyle.font;
      locationLabel.alignment = { horizontal: 'right', vertical: 'top' };

      // Add empty bordered cells for label column in definition rows
      const maxLocations = Math.max(...selectedMAs.map(ma => (ma.locations || []).length), 1);
      for (let i = 1; i < maxLocations; i++) {
        const rowNum = DEFINITION_START_ROW + i;
        const labelCell = worksheet.getCell(rowNum, 2);
        labelCell.value = '';
      }

      // Generate and download file
      const excelBuffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const finalFileName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
      saveAs(blob, finalFileName);

      toast.dismiss(loadingToast);
      const exportedCount = Math.min(selectedMAs.length, MA_COLUMNS.length);
      if (selectedMAs.length > MA_COLUMNS.length) {
        toast.success(`Exported ${exportedCount} of ${selectedMAs.length} market area(s). Template supports max ${MA_COLUMNS.length} areas.`);
      } else {
        toast.success(`Exported ${exportedCount} market area(s) successfully`);
      }
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      toast.dismiss(loadingToast);
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Transition.Child
          enter="transition ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          as={Fragment}
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <Transition.Child
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
          as={Fragment}
        >
          <Dialog.Panel className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-6 relative z-10">
            <Dialog.Title className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Export Market Areas to Excel
            </Dialog.Title>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Export market areas using the formatted template with all styling preserved (max 9 areas per file). Only location-based market areas can be exported.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  File Name
                </label>
                <input
                  type="text"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="market_areas_export"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Market Areas ({exportableMarketAreas.length} exportable)
                </label>

                {exportableMarketAreas.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                    No exportable market areas found. Only ZIP, Place, County, Tract, Block Group, Block, CBSA, and State types can be exported.
                  </p>
                ) : (
                  <>
                    <div className="mb-2">
                      <input
                        type="checkbox"
                        checked={selectedMAIds.length === exportableMarketAreas.length && exportableMarketAreas.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Select All</span>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-2 border p-2 rounded-md dark:border-gray-600">
                      {exportableMarketAreas.map((ma) => (
                        <div key={ma.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedMAIds.includes(ma.id)}
                            onChange={() => handleToggleMA(ma.id)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          />
                          <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            {ma.name}
                            <span className="text-xs text-gray-500 ml-1">
                              ({MA_TYPE_TO_DISPLAY[ma.ma_type] || ma.ma_type})
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isExporting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700
                  border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting || selectedMAIds.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}
